/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * Form
 */

import {
  Heading,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Modal,
  VStack,
  Text,
  Button,
  ModalOverlay,
} from "@chakra-ui/react";
import TextField from "components/base/atoms/TextField";
import { SecondaryButton } from "../base/atoms/HJSButton";
import AddressAutocompleteTextfield from "components/AddressAutocompleteTextfield";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAddressDataQuery } from "_actions/AuthActions";
import useAuth from "hooks/useAuth";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MessageDialog from "components/MessageDialog";

interface IModalComponentProps {
  open: boolean;
  handleClose: () => void;
}
interface IFormInput {
  firstName: string;
  lastName: string;
  street: string;
  // phone: string;
  city: string;
  pobox: string;
  addressLine: string;
  state: string;
  zip_code: string;
}
type TModalComponent = (props: IModalComponentProps) => JSX.Element;
const addressFormSchema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  pobox: yup
    .string()
    // .typeError("Streak and Goals Target is required")
    .nullable()
    .test(
      "Start Date conditional validation",
      "P.O Box Number is required ",
      (defaultDate, { parent }) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return Boolean(defaultDate) || Boolean(parent.street);
      }
    ),
  street: yup
    .string()
    // .typeError("Streak and Goals Target is required")
    .nullable()
    .test(
      "Start Date conditional validation",
      "Street is required ",
      (defaultDate, { parent }) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return Boolean(defaultDate) || Boolean(parent.pobox);
      }
    ),
  city: yup.string().required("City is required"),
  state: yup.string().required("State is required"),
  zip_code: yup.string().required("Zip code is required"),
});
const AddressFormModal: TModalComponent = ({
  open,
  handleClose,
}: IModalComponentProps) => {
  const {
    register,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormInput>({
    resolver: yupResolver(addressFormSchema),
  });
  const formValues = watch();
  const { mutate: addressUserData, isLoading } = useAddressDataQuery();
  const { logout } = useAuth();
  const [openModal, setOpenModal] = useState(false);

  const closeAndReset = (): void => {
    handleClose();
    const isSearchJobPage = location?.pathname !== "/survey/personality";
    if (isSearchJobPage) {
      setOpenModal(true);
    }
    reset();
  };

  const handleCloseModal = (): void => {
    setOpenModal(false);
  };

  const handleLogout: () => void = () => {
    logout();
    handleClose();
  };

  const submitHandler: (values: IFormInput) => void = (values: IFormInput) => {
    const payload = {
      address: {
        first_name: values?.firstName,
        last_name: values?.lastName,
        city: values?.city,
        state: values?.state,
        zip_code: values?.zip_code,
        street: values?.street,
        poboxNumber: values?.pobox,
        addressLine: values?.addressLine,
        // phone: values?.phone,
      },
    };

    addressUserData(
      { ...payload },
      {
        onSuccess: () => {
          closeAndReset();
        },
      }
    );
  };

  const handleGoogleAddress = (address: any): void => {
    setValue("state", address.state, { shouldValidate: true });
    setValue("street", address.address, { shouldValidate: true });
    setValue("city", address.city, { shouldValidate: true });
    setValue("zip_code", address.postal_code, { shouldValidate: true });
  };
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "pobox" || name === "street") {
        // Trigger validation for both fields
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        trigger(["pobox", "street"]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const handleManualStreetAddress = (street: any): void => {
    setValue("street", street);
  };

  return (
    <>
      <MessageDialog open={openModal} handleClose={handleCloseModal} />

      <form onSubmit={handleSubmit(submitHandler)}>
        <Modal
          isOpen={open}
          allowPinchZoom
          blockScrollOnMount={true}
          closeOnOverlayClick={false}
          closeOnEsc={false}
          isCentered
          onClose={() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            null;
          }}
        >
          <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" />
          <ModalContent>
            <ModalHeader>
              <VStack align="flex-start">
                <Heading as="h2" fontSize={[20, 20, 30, 40]} color="teal.900">
                  Address
                </Heading>
                <Text
                  color="hjsBlack"
                  fontWeight="500"
                  fontSize={[12, 12, 18, 14]}
                >
                  Please provide your address. This is the address where we will
                  mail the checks with your earnings.{" "}
                  {/* <SecondaryButton
                  onClick={handleLogout}
                  fontSize={[12, 12, 12, 16]}
                >
                  Log Out
                </SecondaryButton> */}
                  <Button
                    size="xl"
                    fontWeight="500"
                    color="hjsBlack"
                    variant="link"
                    onClick={handleLogout}
                    className="!underline"
                  >
                    Log Out
                  </Button>{" "}
                </Text>
              </VStack>
              {/* <Flex direction="row" justify="space-between" h="full">
              <Heading as="h1" fontSize={[20, 20, 30, 40]} color="teal.900">
                Address
                <Text
                  color="hjsBlack"
                  fontWeight="100"
                  fontSize={[6, 6, 8, 16]}
                >
                  Please introduce your address. This would be the address where
                  we will mail the checks with your earnings.
                </Text>
              </Heading>
            </Flex> */}
            </ModalHeader>

            <ModalBody overflowY="auto" maxH="calc(100vh - 280px)">
              <VStack mt={4} alignItems="flex-start" gap={4}>
                <TextField
                  {...register("firstName")}
                  label="First Name *"
                  placeholder="Enter a first name"
                  type="text"
                  value={formValues?.firstName ?? ""}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message}
                />{" "}
                <TextField
                  {...register("lastName")}
                  label="Last Name *"
                  placeholder="Enter a last name"
                  type="text"
                  value={formValues?.lastName ?? ""}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message}
                />
                {/* <TextField
                  {...register("phone")}
                  label="Phone Number"
                  placeholder="Enter a phone number"
                  value={formValues?.phone ?? ""}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                /> */}
                <AddressAutocompleteTextfield
                  placeholder="Enter a street address"
                  label="Street Address"
                  InputLabelProps={{ style: { fontSize: 14 } }}
                  inputProps={{ maxLength: 50 }}
                  fullWidth
                  value={formValues?.street}
                  handleManualStreetAddress={handleManualStreetAddress}
                  handleGoogleAddress={handleGoogleAddress}
                  peronsalAddress="true"
                  error={Boolean(errors.street)}
                  helperText={errors.street?.message}
                />
                <TextField
                  {...register("addressLine")}
                  label="Address Line 2"
                  placeholder="(e.g. Apt,Unit)"
                  type="text"
                  value={formValues?.addressLine ?? ""}
                  error={Boolean(errors.addressLine)}
                  helperText={errors.addressLine?.message}
                />
                <TextField
                  {...register("pobox")}
                  label="P.O box number"
                  placeholder="Enter a P.O Box number"
                  type="text"
                  value={formValues?.pobox ?? ""}
                  error={Boolean(errors.pobox)}
                  helperText={errors.pobox?.message}
                />
                <TextField
                  {...register("city")}
                  label="City *"
                  placeholder="Enter a city"
                  type="text"
                  value={formValues?.city ?? ""}
                  error={Boolean(errors.city)}
                  helperText={errors.city?.message}
                />
                <TextField
                  label="State *"
                  {...register("state")}
                  error={Boolean(errors.state)}
                  helperText={errors.state?.message}
                  placeholder="Enter a state"
                  type="text"
                  value={formValues?.state ?? ""}
                />
                <TextField
                  label="Zip code *"
                  {...register("zip_code")}
                  error={Boolean(errors.zip_code)}
                  helperText={errors.zip_code?.message}
                  placeholder="Enter a zip code"
                  value={formValues?.zip_code ?? ""}
                  type="text"
                />
              </VStack>
            </ModalBody>
            <ModalFooter alignItems="center" justifyContent="center">
              <SecondaryButton
                my={4}
                onClick={handleSubmit(submitHandler)}
                type="submit"
                px={16}
                isLoading={isLoading}
                fontSize={[12, 12, 14, 16]}
              >
                Next
              </SecondaryButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </form>
    </>
  );
};

export default AddressFormModal;
